import { memo, useCallback } from 'react';
import { Calendar, User, Users } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Avatar, Badge, Button, Card } from '@aimsos/ui';

import { formatListCardDotDate, formatWorkspaceStatusLabel, isWorkspaceStatusActive } from '../utils';

const INITIALS_BACKGROUNDS = [
  'bg-[#2173FF] text-white',
  'bg-[#7C3AED] text-white',
  'bg-[#0891B2] text-white',
  'bg-[#C026D3] text-white',
];

const workspaceInitials = (name) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
};

const initialsBackgroundClass = (name) => {
  let sum = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
  return INITIALS_BACKGROUNDS[sum % INITIALS_BACKGROUNDS.length];
};

const statusBadgeVariant = (status) =>
  isWorkspaceStatusActive(status) ? 'success' : 'neutral';

/**
 * Workspace tile for the list grid — Card + Avatar + Badge + Button (@aimsos/ui).
 */
const WorkspaceGridCard = memo(({ workspace, onViewDetails }) => {
  const { t, i18n } = useTranslation('workspaces');

  const handleCardActivate = useCallback(() => {
    onViewDetails?.(workspace);
  }, [onViewDetails, workspace]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardActivate();
      }
    },
    [handleCardActivate],
  );

  const handleDetailsClick = useCallback(
    (e) => {
      e.stopPropagation();
      onViewDetails?.(workspace);
    },
    [onViewDetails, workspace],
  );

  const ownerLine = workspace.ownerName?.trim() || t('list.card.noOwner');
  const dateSource = workspace.updatedAt ?? workspace.createdAt;
  const dateLine = formatListCardDotDate(dateSource, i18n.language);
  const hasStatus = Boolean(workspace.status?.trim());

  return (
    <Card
      className="flex cursor-pointer flex-col p-5 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2173FF] focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400"
      role="button"
      tabIndex={0}
      aria-label={workspace.name}
      onClick={handleCardActivate}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Avatar
            src={workspace.profilePhotoUrl || undefined}
            initials={workspace.profilePhotoUrl ? undefined : workspaceInitials(workspace.name)}
            alt={workspace.name}
            size="xl"
            className={[
              '!h-12 !w-12 !min-h-[3rem] !min-w-[3rem] shrink-0',
              workspace.profilePhotoUrl
                ? ''
                : ['text-sm font-semibold text-white', initialsBackgroundClass(workspace.name)].join(' '),
            ]
              .filter(Boolean)
              .join(' ')}
          />
          <h3 className="min-w-0 flex-1 pt-1 text-base font-semibold leading-snug text-content">
            {workspace.name}
          </h3>
        </div>
        {hasStatus ? (
          <Badge
            variant={statusBadgeVariant(workspace.status)}
            size="small"
            className="!rounded-full shrink-0"
          >
            {formatWorkspaceStatusLabel(workspace.status, t)}
          </Badge>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-content-muted">
        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
          <User size={14} className="shrink-0 text-content-faint" aria-hidden />
          <span className="truncate">{ownerLine}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <Calendar size={14} className="shrink-0 text-content-faint" aria-hidden />
          {dateLine}
        </span>
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <Users size={14} className="shrink-0 text-content-faint" aria-hidden />
          {workspace.membersCount}
        </span>
      </div>

      <Card.Footer className="mt-4 !border-t !border-edge !px-0 !py-4">
        <div className="flex justify-end">
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            className="!h-auto min-h-0 rounded-none px-0 py-0 text-sm font-normal text-content underline shadow-none hover:bg-transparent hover:underline focus-visible:ring-0 focus-visible:ring-offset-0 dark:hover:bg-transparent"
            onClick={handleDetailsClick}
          >
            {t('list.card.viewDetails')}
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
});

WorkspaceGridCard.displayName = 'WorkspaceGridCard';

export default WorkspaceGridCard;
